import { useCallback, useState, useMemo, useRef } from 'react';
import { useRequest } from 'umi';

import {
  getJavaCities,
  getOrderBase as getOrderBaseApi,
  getStandardVehicleList as getStandardVehicleListApi,
  getCoupeData,
  getEpVersionAndPackage,
  getEpRelationIdentityApi,
} from '@/apis/apis';
import { isEmpty, isEmptyObject } from '@hll/eapp-utils';
import { isStaticPage } from '@/utils/static';
import { getEpAuthQuery } from '@/apis/eoc-soa-api';

export type CitiesType = {
  id: string;
  name: string;
};

export type OrderBaseType = {
  code: number;
  desc: string;
  sub?: any;
};

export type StandardVehicleType = {
  standardVehicleType: number;
  standardVehicleTypeName: string;
  vehicleType?: any;
  vehicleTypeName?: string;
};

export type CoupeDataType = {
  securityDetailCanJump?: boolean;
  maxCostLimit?: number;
  epTitleReg?: string;
  walletConfig?: {
    payAgreementTemp: string;
    payAgreementExp: string;
  };
  authorizationAgreement?: string; // 个人代付协议
  authorizationAgreementExp?: string; // 示例图片
  isZhimaVerifiedVisible?: boolean;
  isShowFeedback?: boolean;
  isLegalPersonRequired?: boolean; // 法定代表人
  isFileRequired?: boolean; // 营业执照
  /** 开票是否开启校验：企业认证和营业执照 */
  verifyAndLicenseOpen?: boolean;
  /** 收取件码图片描述 */
  pickupReceiveCodeDesc?: string;
  /** 发票常量配置 JSON 对象 */
  invoiceConstant?: {
    applyMax?: number;
    quickApplyMax?: number;
    [key: string]: any;
  };
};
export type EpRelationIdentityConfigType = {
  /** 关联状态 1: 未关联 2: 待同意 3: 子企业 4: 母企业 5: 准母企业 */
  epIdentity?: 1 | 2 | 3 | 4 | 5;
};

export type EpAuthDataType = {
  /** 企业资质认证状态(1:未认证,2:认证中,3:认证通过,4:认证未通过) */
  verify?: number;
  /** 个人代付授权协议状态(-1:未提交,1:审核中,2:审核通过3:审核未通过) */
  personalPayAgreeStatus?: number;
  personalPayAgreePicPath?: string;
  /** 客户属性(0:未确认,1:企业,2:机构/个人) */
  epType?: string;
  /** 交易频次(0:未知,1:低,2:中,3:高) */
  transactionFrequency?: number;
};
export default function Common() {
  // pagani 城市列表
  const [javaCities, setJavaCities] = useState<CitiesType[]>([]);
  const setJavaCitiesCb = useCallback((val) => {
    setJavaCities(val);
  }, []);
  const getCities = useRequest(getJavaCities, {
    manual: true,
    onSuccess: (data: CitiesType[]) => {
      setJavaCitiesCb(data);
    },
  }).run;

  // 支付方式
  const [payTypeList, setPayTypeList] = useState<OrderBaseType[]>([]);
  const setPayTypeListCb = useCallback((val) => {
    setPayTypeList(val);
  }, []);
  // 订单状态
  const [orderStatusList, setOrderStatusList] = useState<OrderBaseType[]>([]);
  const setOrderStatusListCb = useCallback((val) => {
    setOrderStatusList(val);
  }, []);
  const getOrderBase = useRequest(getOrderBaseApi, {
    manual: true,
    onSuccess: (data) => {
      setOrderStatusListCb(data.orderStatusList);
      setPayTypeListCb(data.payTypeList);
    },
  }).run;

  // 国标车型
  const [standardVehicleList, setStandardVehicleList] = useState<
    StandardVehicleType[]
  >([]);
  const setStandardVehicleListCb = useCallback((val) => {
    setStandardVehicleList(val);
  }, []);
  const getStandardVehicleList = useRequest(getStandardVehicleListApi, {
    manual: true,
    onSuccess: (data) => {
      setStandardVehicleListCb(data);
    },
  }).run;

  // coupe配置
  const [coupeData, setCoupeData] = useState<CoupeDataType>({});
  let coupeLoading = false; // 避免同时请求loading
  const initCoupeData = async () => {
    if (coupeLoading) return;
    coupeLoading = true;
    const data = await getCoupeData<CoupeDataType & { invoiceConstant?: string | Record<string, unknown> }>({
      coupeId: '1e649c2c4df345388cfcb96418df16c3',
    });
    
    // 处理 invoiceConstant 字段：如果是字符串则解析为对象
    if (data && typeof data.invoiceConstant === 'string') {
      try {
        data.invoiceConstant = JSON.parse(data.invoiceConstant);
      } catch (error) {
        // 解析失败时设置为空对象，避免影响其他功能
        console.warn('invoiceConstant 解析失败:', error);
        data.invoiceConstant = {};
      }
    }
    
    setCoupeData(data);
  };
  const coupeDataProxy = new Proxy(
    { coupeData },
    {
      get(obj: { coupeData: CoupeDataType }, prop: string, receiver) {
        const res = Reflect.get(obj, prop, receiver);
        if (!isEmptyObject(res)) {
          coupeLoading = false;
        } else {
          initCoupeData();
        }
        return res;
      },
    }
  );
  // 企业版本、权益包、运营模式
  const [epVersionObj, setEpVersionObj] = useState<any>({});
  const epVersionLoading = useRef(false);
  const getEpVersion = async () => {
    // 在静态页面（非强制登录页面）不需要请求getEpVersion 否则可能会提示未登录
    if (isStaticPage()) {
      return;
    }

    if (epVersionLoading.current) {
      return;
    }

    const requestParams = {
      loginIntroduceAndModeSelect: 1,
    };
    try {
      epVersionLoading.current = true;
      const data: any = await getEpVersionAndPackage(requestParams);
      if (data?.ret === 0) {
        setEpVersionObj(data.data);
      }
    } catch {
      console.log('ep.version.getInfoByEpId error');
    } finally {
      epVersionLoading.current = false;
    }
  };
  const epVersionObjProxy = new Proxy(
    { epVersionObj },
    {
      get(obj: Record<string, any>, prop: string, receiver) {
        const res = Reflect.get(obj, prop, receiver);
        if (isEmptyObject(res)) {
          getEpVersion();
        }
        return res;
      },
    }
  );

  const isMatchType = useMemo(() => {
    return epVersionObjProxy?.epVersionObj?.modeType === 1;
  }, [epVersionObjProxy?.epVersionObj?.modeType]);
  const isClearType = useMemo(() => {
    return epVersionObjProxy?.epVersionObj?.isClearRequire === 1;
  }, [epVersionObjProxy?.epVersionObj?.isClearRequire]);

  // 获取企业关联信息
  const [epIdentityConfig, setEpIdentityConfig] =
    useState<EpRelationIdentityConfigType>({});
  const [epIdentityConfigLoading, setEpIdentityConfigLoading] =
    useState<boolean>(false);

  const getEpIdentityConfig = async () => {
    if (epIdentityConfigLoading) return;
    setEpIdentityConfigLoading(true);
    const data: any = await getEpRelationIdentityApi();
    if (data?.ret === 0) {
      setEpIdentityConfig(data?.data);
    }
  };
  const epIdentityConfigProxy = new Proxy(
    { ...epIdentityConfig },
    {
      get(obj: EpRelationIdentityConfigType, prop: string, receiver) {
        const res = Reflect.get(obj, prop, receiver);
        if (isEmpty(res)) {
          // 延迟执行setState，避免渲染期间调用
          setTimeout(() => getEpIdentityConfig(), 0);
        } else {
          // 延迟执行setState，避免渲染期间调用
          setTimeout(() => setEpIdentityConfigLoading(false), 0);
        }
        return res;
      },
    }
  );

  const [epAuthData, setEpAuthData] = useState<EpAuthDataType>({});
  const epAuthLoading = useRef(false);

  const getEpAuthPromise = async (isForce?: boolean) => {
    // console.log('>>> ~ getEpAuthPromise:', isForce, epAuthLoading.current, epAuthData, isEmptyObject(epAuthData));
    if (!isForce && (epAuthLoading.current || !isEmptyObject(epAuthData))) {
      console.log('getEpAuthPromise -> isForce');
      return Promise.resolve(epAuthData);
    }
    try {
      epAuthLoading.current = true;
      const data: any = await getEpAuthQuery();
      console.log('>>> ~ getEpAuthPromise ~ data:', data);
      if (data?.ret === 0) {
        setEpAuthData(data.data);
      }
      return Promise.resolve(data);
    } catch {
      // console.log('/eucSoa/epAuthData/query error');
      return Promise.reject(new Error('网络错误'));
    } finally {
      // console.log("getEpAuthPromise -> finally ")
      epAuthLoading.current = false;
    }
  };
  const epAuthQueryProxy = new Proxy(
    { ...epAuthData },
    {
      get(obj: EpAuthDataType, prop: string, receiver) {
        const res = Reflect.get(obj, prop, receiver);
        if (isEmptyObject(epAuthData)) {
          getEpAuthPromise();
        }
        return res;
      },
    }
  );

  return {
    getCities,
    javaCities,
    setJavaCitiesCb,

    getOrderBase,
    payTypeList,
    setPayTypeListCb,
    orderStatusList,
    setOrderStatusListCb,

    getStandardVehicleList,
    standardVehicleList,
    setStandardVehicleListCb,

    coupeDataProxy,

    epVersionObj,
    setEpVersionObj,
    epVersionObjProxy,
    getEpVersion,
    epVersionLoading,
    isMatchType,
    isClearType,
    /** 企业关联loading */
    epIdentityConfigLoading,

    /** 企业关联信息 */
    epIdentityConfigProxy,

    /** 手动获取获取企业关联信息【用于获取最新状态】 */
    getEpIdentityConfig,

    epAuthData,
    epAuthQueryProxy,
    getEpAuthPromise,
  };
}
